import SkeletonComponent from '../../SkeletonComponent';
import { AvlTable } from '../Table/Table';
import { Alert, CircularProgress, Stack, Switch } from '@mui/material';
import { useRef, useState } from 'react';
import { datetimeToFormattedString } from '../../../Utils/Utils';
import Box from '@mui/material/Box';
import DownloadIcon from '@mui/icons-material/Download';
import { green } from '@mui/material/colors';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import InventoryIcon from '@mui/icons-material/Inventory';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { constCheckFolder, initiateFolderGeneration, setFolderMaintenance } from '../../../Services/Search.services';
import MaintenanceModeModal from '../MaintenanceModeModal/MaintenanceModeModal';
import ChangeEmail from '../ChangeEmail';
import FileInformation from '../FileInformation';
import FolderAudits from '../FolderAudits';
import GenerateFolderReports from '../GenerateFolderReports';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { UrlActionType, UrlButton } from '../UrlButton/UrlButton';
import PropTypes from 'prop-types';
import { AvlModal } from '../AvlModal/AvlModal';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FolderDeleteOutlinedIcon from '@mui/icons-material/FolderDeleteOutlined';
import { Apps } from '../Apps';
import { shortId } from '../../../Utils/Utils';
import ChangeOwnerTable from '../ChangeOwnerTable';
import PurchasesModal from '../PurchasesModal';

export const Columns = {
    projectName: 'Project Name',
    folderId: 'Folder ID',
    titlesAmount: 'Titles',
    scottishTitlesAmount: 'Scottish Titles',
    leasesAmount: 'Leases',
    plansAmount: 'Plans',
    scottishPlansAmount: 'Scottish Plans',
    othersAmount: 'Others',
    username: 'Username',
    matterNumber: 'Matter Number',
    teamworkAccess: 'Teamwork Access',
    createdAt: 'Created At',
    lastOpenedAt: 'Last Opened',
    lastUpdatedAt: 'Last Updated',
    isDeleted: 'Is Deleted',
    download: 'Download',
    changeOwnership: 'Change Ownership',
    inspect: 'Inspect',
    purchases: 'Purchases',
    audit: 'Audit',
    generate: 'Generate',
    apps: 'Apps',
    maintenance: 'Maintenance',
};

export function ProjectsTable(
    {
        records,
        isLoading,
        page,
        pageCount,
        onPageChange,
        onRefreshRecords,
        hiddenColumns = [],
        projectsOwnerEmail,
    },
) {
    const myRefs = useRef([]);
    const myLoadingRefs = useRef([]);
    const [isChangeEmailPopupOpen, setIsChangeEmailPopupOpen] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [selectedProjectApp, setSelectedProjectApp] = useState('title');
    const [selectedRowEmail, setSelectedRowEmail] = useState('');
    const [isInspectPopupOpen, setIsInspectPopupOpen] = useState(false);
    const [isPurchasesPopupOpen, setIsPurchasesPopupOpen] = useState(false);
    const [isAuditPopupOpen, setIsAuditPopupOpen] = useState(false);
    const [isGeneratePopupOpen, setIsGeneratePopupOpen] = useState(false);
    const [isMaintenanceDialogOpen, setIsMaintenanceDialogOpen] = useState(false);
    const [maintenanceLoadingIds, setMaintenanceLoadingIds] = useState(new Set());
    const [maintenanceOptimistic, setMaintenanceOptimistic] = useState({});

    const handleCloseChangeEmailPopup = () => setIsChangeEmailPopupOpen(false);

    const handleCloseInspect = () => setIsInspectPopupOpen(false);

    const handleClosePurchases = () => setIsPurchasesPopupOpen(false);

    const handleCloseAudit = () => setIsAuditPopupOpen(false);

    const handleCloseGenerate = () => setIsGeneratePopupOpen(false);

    const handleMaintenanceToggle = async (row) => {
        if (row.isMaintenance) {
            setMaintenanceLoadingIds((prev) => new Set(prev).add(row.folderId));

            try {
                await setFolderMaintenance(row.folderId, { is_maintenance: false, user_name: '' });
                setMaintenanceOptimistic((prev) => ({ ...prev, [row.folderId]: false }));
            } finally {
                setMaintenanceLoadingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(row.folderId);

                    if (!next.size) {
                        onRefreshRecords();
                        setMaintenanceOptimistic({});
                    }

                    return next;
                });
            }
        } else {
            setSelectedProjectId(row.folderId);
            setIsMaintenanceDialogOpen(true);
        }
    };

    const handleDownload = async (downUrl, projectName, index) => {
        myRefs.current[index].style.disabled = true;
        myLoadingRefs.current[index].style.display = 'block';
        myRefs.current[index].style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
        myRefs.current[index].style.color = 'rgba(0, 0, 0, 0.26)';
        myRefs.current[index].style.borderColor = 'rgba(0, 0, 0, 0.26)';

        await initiateFolderGeneration(downUrl)
            .then((downloadUrl) => {
                constCheckFolder(downloadUrl, projectName)
                    .then((boolean) => {
                        if (boolean) {
                            myRefs.current[index].style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
                            myRefs.current[index].style.color = 'rgb(25, 118, 210)';
                            myRefs.current[index].style.borderColor = 'rgb(25, 118, 210)';
                            myRefs.current[index].style.disabled = false;
                            myLoadingRefs.current[index].style.display = 'none';
                        } else {
                            myRefs.current[index].style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
                            myRefs.current[index].style.color = 'rgb(25, 118, 210)';
                            myRefs.current[index].style.borderColor = 'rgb(25, 118, 210)';
                            myRefs.current[index].style.disabled = false;
                            myLoadingRefs.current[index].style.display = 'none';
                        }
                    });
            });
    };

    const tableHeaders = [
        {
            label: Columns.projectName,
            align: 'left',
        },
        {
            label: Columns.folderId,
            align: 'left',
        },
        {
            label: Columns.titlesAmount,
            align: 'center',
        },
        {
            label: Columns.scottishTitlesAmount,
            align: 'left',
        },
        {
            label: Columns.leasesAmount,
            align: 'center',
        },
        {
            label: Columns.plansAmount,
            align: 'center',
        },
        {
            label: Columns.scottishPlansAmount,
            align: 'left',
        },
        {
            label: Columns.othersAmount,
            align: 'center',
        },
        {
            label: Columns.username,
            align: 'left',
        },
        {
            label: Columns.matterNumber,
            align: 'left',
        },
        {
            label: Columns.teamworkAccess,
            align: 'left'
        },
        {
            label: Columns.createdAt,
            align: 'left',
        },
        {
            label: Columns.lastOpenedAt,
            align: 'left',
        },
        {
            label: Columns.lastUpdatedAt,
            align: 'left',
        },
        {
            label: Columns.isDeleted,
            align: 'center',
        },
        {
            label: Columns.download,
            align: 'center',
        },
        {
            label: Columns.changeOwnership,
            align: 'center',
        },
        {
            label: Columns.inspect,
            align: 'center',
        },
        {
            label: Columns.purchases,
            align: 'center'
        },
        {
            label: Columns.audit,
            align: 'center',
        },
        {
            label: Columns.generate,
            align: 'center',
        },
        {
            label: Columns.apps,
            align: 'left',
        },
        {
            label: Columns.maintenance,
            align: 'center',
        },
    ];
    const filteredHeaders = tableHeaders.filter((header) => !hiddenColumns.includes(header.label));

    const renderMaintenanceCell = (row) => {
        const isMaintenance = row.folderId in maintenanceOptimistic
            ? maintenanceOptimistic[row.folderId]
            : row.isMaintenance;

        return (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Tooltip title={isMaintenance ? 'Turn off Maintenance Mode' : 'Turn on Maintenance Mode'}>
                    <span>
                        <Switch
                            checked={!!isMaintenance}
                            onChange={() => handleMaintenanceToggle(row)}
                            disabled={row.isDeleted || maintenanceLoadingIds.has(row.folderId)}
                            size='small'
                        />
                    </span>
                </Tooltip>
                {maintenanceLoadingIds.has(row.folderId) && (
                    <CircularProgress
                        size={20}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-10px',
                            marginLeft: '-10px',
                        }}
                    />
                )}
            </Box>
        );
    };

    const convertToCells = (row, rowIndex) => [
        {
            isDisabled: hiddenColumns.includes(Columns.projectName),
            data: row.projectName || 'N/A',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.folderId),
            data: row.folderId
                ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '65px' }}>
                            {row.folderId}
                        </div>
                        <UrlButton
                            buttonStyles={{
                                link: { width: '32px', height: '32px' },
                            }}
                            description={`Copy to clipboard "${row.folderId}"`}
                            action={UrlActionType.copyToClipboard}
                            url={row.folderId}
                        />
                    </div>
                )
                : 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.titlesAmount),
            align: 'center',
            data: row.numberOfTitles || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.scottishTitlesAmount),
            align: 'left',
            data: row.numberOfScottishTitles || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.leasesAmount),
            align: 'center',
            data: row.numberOfLeases || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.plansAmount),
            align: 'center',
            data: row.numberOfPlans || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.scottishPlansAmount),
            align: 'left',
            data: row.numberOfScottishPlans || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.othersAmount),
            align: 'center',
            data: row.numberOfOthers || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.username),
            data: row.username || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.matterNumber),
            data: row.matterNumber || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.teamworkAccess),
            data: row.isPrivate
                ? (row.members.length
                    ? (
                        <Tooltip title={row.members.join(', ')}>
                            <div style={{
                                maxWidth: '200px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                Open to: {row.members.join(', ')}
                            </div>
                        </Tooltip>
                    )
                    : 'Private'
                )
                : 'Open to Organisation'
        },
        {
            isDisabled: hiddenColumns.includes(Columns.createdAt),
            style: { minWidth: '90px' },
            data: datetimeToFormattedString(row.createdAt, 'None'),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.lastOpenedAt),
            style: { minWidth: '90px' },
            data: datetimeToFormattedString(row.lastOpenedAt, 'None'),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.lastUpdatedAt),
            style: { minWidth: '90px' },
            data: datetimeToFormattedString(row.lastUpdatedAt, 'None'),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.isDeleted),
            align: 'center',
            data: row.isDeleted.toString() && (
                <Tooltip title="The folder is deleted">
                    <FolderDeleteOutlinedIcon />
                </Tooltip>
            ),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.download),
            align: 'center',
            data: (
                <Box sx={{ m: 1, position: 'relative' }}>
                    <Tooltip title="Download">
                        <span>
                            <IconButton
                                disabled={row.isDeleted}
                                id={'button' + rowIndex}
                                ref={(el) => (myRefs.current[rowIndex] = el)}
                                onClick={() => handleDownload(row.attachments, row.projectName, rowIndex)}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <CircularProgress
                        size={24}
                        ref={(el) => (myLoadingRefs.current[rowIndex] = el)}
                        sx={{
                            color: green[500],
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                            display: 'none',
                        }}
                    />
                </Box>
            ),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.changeOwnership),
            align: 'center',
            data: (
                <Tooltip title="Change ownership">
                    <span>
                        <IconButton
                            disabled={row.isDeleted}
                            onClick={() => {
                                setSelectedRowEmail(projectsOwnerEmail || row.username);
                                setSelectedProjectId(row.folderId);
                                setIsChangeEmailPopupOpen(true);
                            }}
                        >
                            <PublishedWithChangesIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.inspect),
            align: 'center',
            data: (
                <Tooltip title="Inspect">
                    <span>
                        <IconButton
                            disabled={row.isDeleted}
                            onClick={() => {
                                setSelectedProjectId(row.folderId);
                                setIsInspectPopupOpen(true);
                            }}
                        >
                            <ZoomInIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.purchases),
            align: 'center',
            data: (
                <Tooltip title="Purchases">
                    <span>
                        <IconButton
                            disabled={row.isDeleted}
                            onClick={() => {
                                setSelectedProjectId(row.folderId);
                                setIsPurchasesPopupOpen(true);
                            }}
                        >
                            <ShoppingCartIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.audit),
            align: 'center',
            data: (
                    <Tooltip title="Audit">
                        <span>
                            <IconButton
                                disabled={row.isDeleted}
                                onClick={() => {
                                    setSelectedProjectId(row.folderId);
                                    setSelectedProjectApp(row.apps);
                                    setIsAuditPopupOpen(true);
                                }}
                            >
                                <InventoryIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
            ),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.generate),
            align: 'center',
            data: (
                <Tooltip title="Generate">
                    <span>
                        <IconButton
                            disabled={row.isDeleted}
                            onClick={() => {
                                setSelectedProjectId(row.folderId);
                                setIsGeneratePopupOpen(true);
                            }}
                        >
                            <SupportAgentIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
        {
            isDisabled: hiddenColumns.includes(Columns.apps),
            data: <Apps apps={[row.apps]} /> || 'None',
        },
        {
            isDisabled: hiddenColumns.includes(Columns.maintenance),
            align: 'center',
            data: renderMaintenanceCell(row),
        },
    ];

    const tableRows = records.map((row, rowIndex) => ({
        cells: convertToCells(row, rowIndex)
            .filter((column) => !column.isDisabled),
    }));

    const getProject = (folderId) => {
        const project = records.find((row) => row.folderId === folderId);
        return project;
    };

    return (
        <>
            {
                isLoading
                    ? (<SkeletonComponent />)
                    : (
                        records.length > 0
                            ? (
                                <AvlTable
                                    headers={filteredHeaders}
                                    rows={tableRows}
                                    page={page}
                                    pageCount={pageCount}
                                    onPageChange={onPageChange}
                                />
                            )
                            : (
                                <Stack sx={{ width: '100%' }} spacing={2}>
                                    <Alert severity="warning">Records not found!</Alert>
                                </Stack>
                            )
                    )
            }
            <AvlModal
                isOpen={isChangeEmailPopupOpen && selectedProjectId}
                onClose={handleCloseChangeEmailPopup}
                title="Edit email"
                maxWidth="1200px"
            >
                <ChangeEmail
                    refreshRecords={onRefreshRecords}
                    projectsCurrentUser={selectedRowEmail}
                    selectedProjectId={selectedProjectId}
                    onClose={handleCloseChangeEmailPopup}
                />
                <ChangeOwnerTable folderId={selectedProjectId} />
            </AvlModal>
            <AvlModal
                isOpen={isInspectPopupOpen && selectedProjectId}
                onClose={handleCloseInspect}
                title="Documents"
                maxWidth="2000px"
            >
                <FileInformation
                    folderId={selectedProjectId}
                    onClose={handleCloseInspect}
                />
            </AvlModal>
            <AvlModal
                isOpen={isPurchasesPopupOpen && selectedProjectId}
                onClose={handleClosePurchases}
                title="Purchases"
                maxWidth="2000px"
            >
                <PurchasesModal
                    folderId={selectedProjectId}
                    onClose={handleClosePurchases}
                />
            </AvlModal>
            <AvlModal
                isOpen={isAuditPopupOpen && selectedProjectId}
                onClose={handleCloseAudit}
                title={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Apps apps={[selectedProjectApp]} />
                        <b>Folder Audit</b>
                        <Tooltip
                            title={`Folder ID: ${selectedProjectId}`}
                        >
                            {shortId(selectedProjectId)}
                        </Tooltip>
                        <UrlButton
                            buttonStyles={{
                                link: { width: '32px', height: '32px' },
                            }}
                            description={`Copy to clipboard "${selectedProjectId}"`}
                            action={UrlActionType.copyToClipboard}
                            url={selectedProjectId}
                        />
                        {`${getProject(selectedProjectId)?.projectName}`}
                    </div>
                )}
                maxWidth="2000px"
                isDefaultPaddings={false}
            >
                <FolderAudits
                    folderId={selectedProjectId}
                    onClose={handleCloseAudit}
                />
            </AvlModal>
            <AvlModal
                isOpen={isGeneratePopupOpen}
                onClose={handleCloseGenerate}
                title="Available Reports"
                maxWidth="2000px"
            >
                <GenerateFolderReports
                    folderId={selectedProjectId}
                    onClose={handleCloseGenerate}
                />
            </AvlModal>
            <MaintenanceModeModal
                isOpen={isMaintenanceDialogOpen}
                folderId={selectedProjectId}
                onClose={() => setIsMaintenanceDialogOpen(false)}
                onSuccess={onRefreshRecords}
            />
        </>
    );
}

ProjectsTable.propTypes = {
    records: PropTypes.array.isRequired,
    isLoading: PropTypes.bool.isRequired,
    rows: PropTypes.array.isRequired,
    hiddenColumns: PropTypes.array,
    pageCount: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRefreshRecords: PropTypes.func.isRequired,
    projectsOwnerEmail: PropTypes.string,
};
